/**
 * Pure helpers behind the permissions matrix: the baseline selection, the
 * dirty-state diff, and row filtering. Kept out of the component so they can be
 * tested without rendering the data table.
 */

import { PRODUCT_WIDE_SCOPE } from './grants'
import { ALL_ROLE_KEYS, ROLES_WITHOUT_PHI_CLEARANCE } from './types'
import { CAPABILITIES } from './capabilities'
import type {
  Capability,
  MatrixRow,
  PermissionSelection,
  RoleKey,
} from './types'

/**
 * Every capability, ticked for nobody.
 *
 * The screen shows what the grants table holds and nothing else. Starting from
 * the catalogue's shipped roles would draw boxes for access the server does not
 * give — a capability with no row permits no role — and the operator would be
 * reading the file, not the state.
 */
export function baseline(): PermissionSelection {
  return Object.fromEntries(CAPABILITIES.map((c) => [c.id, [] as RoleKey[]]))
}

/** Roles for a capability; an unknown id reads as no access rather than throwing. */
export function rolesFor(
  selection: PermissionSelection,
  id: string
): RoleKey[] {
  return selection[id] ?? []
}

export function sameRoles(a: RoleKey[], b: RoleKey[]): boolean {
  return a.length === b.length && a.every((role, i) => role === b[i])
}

/** Ids whose roles differ between the edited selection and the saved one. */
export function changedCapabilityIds(
  selection: PermissionSelection,
  saved: PermissionSelection
): string[] {
  return Object.keys(selection).filter(
    (id) => !sameRoles(rolesFor(selection, id), rolesFor(saved, id))
  )
}

/**
 * Capability rows carrying the live selection instead of the inventory default.
 *
 * `orgScopes` rides along for the same reason the roles do: the column defs
 * have to stay stable across edits, so what changes between renders is the
 * data, not the columns.
 */
export function rowsWithSelection(
  selection: PermissionSelection,
  orgScopes?: Record<string, string[]>
): MatrixRow[] {
  const rows: MatrixRow[] = []
  for (const capability of CAPABILITIES) {
    rows.push(
      Object.assign({}, capability, {
        roles: rolesFor(selection, capability.id),
        ...(orgScopes ? { orgs: orgScopes[capability.id] ?? [] } : {}),
      })
    )
  }
  return rows
}

/**
 * The rows one scope may see.
 *
 * `held` is the capability ids the server returned a grant for, or undefined
 * to place no restriction. Careforce is unrestricted: the org column is how a
 * capability gets its first row, so one nobody holds yet still has to be on
 * screen.
 *
 * An organization sees only what it holds. Careforce unticking it deletes that
 * organization's row, and the server then returns no grant for the capability
 * — rendering it anyway would leave a row the org can toggle and the server
 * refuses to save.
 *
 * An empty set therefore shows nothing, which is also what an unresolved load
 * looks like. That is the deliberate direction: a row appears once we can show
 * the org holds it, not on the way to finding out.
 */
export function rowsForScope(
  rows: MatrixRow[],
  held: ReadonlySet<string> | undefined
): MatrixRow[] {
  if (held === undefined) return rows
  return rows.filter((row) => held.has(row.id))
}

/**
 * The row the rest of a page nests under.
 *
 * The catalogue names it where it knows: `pageRow` marks the capability that
 * IS the page, which is the only reliable answer once a page has more than one
 * read on it. Otherwise fall back to the read a route guard binds, which is a
 * good enough guess for the pages that have exactly one.
 *
 * Pages with neither are left flat — nothing on them is reached through
 * anything else here.
 */
function pageGate(
  rows: MatrixRow[],
  enforced: ReadonlySet<string>
): MatrixRow | undefined {
  return (
    rows.find((row) => row.pageRow) ??
    rows.find((row) => enforced.has(row.id) && row.read && !row.write)
  )
}

/** The page a row belongs to, as the expand/collapse state keys it. */
export function pageKeyOf(row: Pick<MatrixRow, 'section' | 'page'>): string {
  return `${row.section}\u0000${row.page}`
}

/**
 * Rows arranged under the capability that opens the page they belong to.
 *
 * Most pages have one read a route guard binds and then a handful of things
 * done from inside: an import is reached from the clients page, so listing it
 * beside the read that opens that page says less than listing it beneath.
 * The governing row leads its page and the rest follow, marked `dependent`.
 *
 * Grant the governing row to nobody and, where the server checks that row,
 * the page leaves the sidebar and refuses its route. Every row on it is
 * marked `pageHidden` then — the rows stay, because an operator deciding
 * whether to reopen the page needs to see what reopening it brings back.
 * A gate no handler consults is not marked: nothing refuses that route.
 *
 * Pages with no governing row are left in catalogue order: nothing on them is
 * reached through anything else here.
 */
export function nestPageRows(
  rows: MatrixRow[],
  enforced: ReadonlySet<string> | undefined
): MatrixRow[] {
  if (!enforced) return rows

  const order: string[] = []
  const byPage = new Map<string, MatrixRow[]>()
  for (const row of rows) {
    const key = pageKeyOf(row)
    const group = byPage.get(key)
    if (group) group.push(row)
    else {
      byPage.set(key, [row])
      order.push(key)
    }
  }

  const nested: MatrixRow[] = []
  for (const key of order) {
    const group = byPage.get(key)!
    const gate = pageGate(group, enforced)
    if (!gate) {
      nested.push(...group)
      continue
    }
    const dependents = group.filter((row) => row.id !== gate.id)
    // Only where the server actually checks the gate. The badge says the
    // route is refused, and for a capability no handler consults that is
    // simply untrue — the page opens whatever the row says.
    //
    // And only where this row's own grant decides the route. A surface that
    // shares a route with a page row — the workflows list, which lives on the
    // Connections page — is absent from that page when nobody holds it, not
    // refused at the door: the page still opens on the other row's grant.
    const pageHidden =
      gate.roles.length === 0 &&
      enforced.has(gate.id) &&
      !rows.some(
        (row) => row.pageRow && row.id !== gate.id && row.route === gate.route
      )
    const mark = pageHidden ? { pageHidden } : {}
    // Only a row that exists to BE the page becomes a bulk control. An
    // inferred gate like data.clients.view is a real capability whose own
    // grant has to stay visible and settable on its own row.
    const pageIds = group.map((row) => row.id)
    const pageRolesAll = ALL_ROLE_KEYS.filter((role) =>
      group.every((row) => row.roles.includes(role))
    )
    const pageRolesSome = ALL_ROLE_KEYS.filter((role) =>
      group.some((row) => row.roles.includes(role))
    )
    // Locked only where every row on the page locks it. Ticking a role the
    // PHI rule closes on some rows still reaches the others; ticking one it
    // closes on all of them would look live and do nothing.
    const pageRolesLocked = ALL_ROLE_KEYS.filter((role) =>
      group.every((row) => rolesLockedByPhi(row.id).includes(role))
    )
    // The union of what the page reaches, so the header cannot claim less
    // than a row beneath it. One product-wide row makes the whole page
    // product-wide; otherwise every slug any row names.
    const scopes = group.map((row) => row.orgs ?? [])
    const pageOrgs = scopes.some((s) => s.includes(PRODUCT_WIDE_SCOPE))
      ? [PRODUCT_WIDE_SCOPE]
      : [...new Set(scopes.flat())].toSorted()
    nested.push({
      ...gate,
      ...mark,
      pageKey: key,
      ...(dependents.length ? { childCount: dependents.length } : {}),
      // Its checkboxes stand for every row on the page: ticked where all
      // agree, mixed where only some do, locked where none may hold it.
      ...(dependents.length && gate.pageRow
        ? { pageIds, pageRolesAll, pageRolesSome, pageRolesLocked, pageOrgs }
        : {}),
    })
    for (const row of dependents) {
      nested.push({ ...row, ...mark, pageKey: key, dependent: true })
    }
  }
  return nested
}

/**
 * Rows to render, given which pages are open.
 *
 * A governing row is always listed; what it contains is listed only while its
 * page is expanded. The row carries its own open state so the column defs can
 * stay stable across a toggle, the same way the roles and orgs do.
 */
export function expandPageRows(
  rows: MatrixRow[],
  expanded: ReadonlySet<string>
): MatrixRow[] {
  const shown: MatrixRow[] = []
  for (const row of rows) {
    const open = row.pageKey !== undefined && expanded.has(row.pageKey)
    if (row.dependent) {
      if (open) shown.push(row)
      continue
    }
    shown.push(row.childCount ? { ...row, expanded: open } : row)
  }
  return shown
}

/** Pages that have anything to expand. */
export function expandablePageKeys(rows: MatrixRow[]): string[] {
  return rows
    .filter((row) => row.childCount && row.pageKey !== undefined)
    .map((row) => row.pageKey as string)
}

export interface MatrixFilters {
  /** Free text matched against page, route and capability. */
  capability?: string
  sections?: string[]
  /** Tokens from the PHI column accessor: 'phi' | 'non-phi'. */
  phi?: string[]
}

export function phiToken(capability: Capability): 'phi' | 'non-phi' {
  return capability.phi ? 'phi' : 'non-phi'
}

export function filterCapabilities(
  rows: Capability[],
  filters: MatrixFilters
): Capability[] {
  const needle = filters.capability?.trim().toLowerCase() ?? ''
  const sections = filters.sections ?? []
  const phi = filters.phi ?? []

  if (!needle && sections.length === 0 && phi.length === 0) return rows

  return rows.filter((row) => {
    if (sections.length > 0 && !sections.includes(row.section)) return false
    if (phi.length > 0 && !phi.includes(phiToken(row))) return false
    if (
      needle &&
      !`${row.page} ${row.route ?? ''} ${row.capability}`
        .toLowerCase()
        .includes(needle)
    ) {
      return false
    }
    return true
  })
}

/**
 * Whether the matrix may edit a row.
 *
 * Every row but an agent tool. Whether a handler consults a given capability
 * yet is a question about how far enforcement has got; who should hold it is
 * the question this screen exists to answer, and recording that decision
 * before the guard catches up is the point.
 *
 * Agent tools are the exception: which tools an agent may call is decided by
 * the per-agent list in Config Manager, not by an org role, so a tick here
 * would answer a question nothing asks of the grants table.
 */
export function isEditable(capability: Capability): boolean {
  return (
    capability.section !== AGENT_TOOLS_SECTION &&
    capability.id !== 'signing.edit'
  )
}

/** Section whose rows are governed by Config Manager rather than by a role. */
const AGENT_TOOLS_SECTION = 'Agent tools'

/**
 * Roles the matrix must not let a PHI capability be granted to.
 *
 * Mirrors the server, which refuses to save a non-PHI-cleared role onto any
 * row that touches patient data, displayed or not.
 *
 * Read from the shipped catalogue rather than the current selection: `phi` is
 * a property of the capability, so the answer must not change as the operator
 * ticks boxes.
 */
export function rolesLockedByPhi(capabilityId: string): RoleKey[] {
  const shipped = CAPABILITIES.find((c) => c.id === capabilityId)
  if (!shipped?.phi) return []
  return ROLES_WITHOUT_PHI_CLEARANCE
}
